import { requireCompletedProfile } from "@hisani/auth";
import { canEditHorseDetails } from "../domain/membership/authorization-policy.js";
import { buildHorseScanFields, missingCompulsoryHorseScanFields, } from "../domain/extraction/horse-scan-extraction.js";
import { beginHorseScanUploadSchema, scanHorseDocumentSchema, stableExtractionUnavailable, stableForbidden, stableNotFound, stableUploadUnavailable, } from "../validation.js";
/** Presigned PUT window a client has to finish a scan-photo upload — mirrors
 * `DOCUMENT_UPLOAD_EXPIRY_SECONDS` in `vet-farrier-record-use-cases.ts`. */
const HORSE_SCAN_UPLOAD_EXPIRY_SECONDS = 300;
function objectKeyPrefix(stableId) {
    return `private/stable/${stableId}/horse-scans/`;
}
function safeFilename(filename) {
    return filename.replace(/[^A-Za-z0-9._-]/g, "-");
}
async function requireHorseEditor(dependencies, stableId, horsemanId) {
    const membership = await dependencies.repository.getMembershipContext(stableId, horsemanId);
    if (membership === null)
        throw stableNotFound();
    if (!canEditHorseDetails(membership))
        throw stableForbidden();
}
/** Begins a presigned upload for a passport/name-plate photo, reusing the
 * Vet/Farrier private-document upload port (`docs/products/stable.md`'s
 * "private object storage with per-request authorization checks" pattern
 * applies just as much to a horse-add scan photo). Restricted to Horse
 * Details editors — the same Entity Roles allowed to add a horse manually. */
export async function beginHorseScanUpload(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = beginHorseScanUploadSchema.parse(input);
    await requireHorseEditor(dependencies, parsed.stableId, actor.account.id);
    const uploads = dependencies.documentUploads;
    if (uploads === undefined)
        throw stableUploadUnavailable();
    const objectKey = `${objectKeyPrefix(parsed.stableId)}${parsed.id}-${safeFilename(parsed.filename)}`;
    const { uploadUrl } = await uploads.createUpload({
        objectKey,
        contentType: parsed.contentType,
        contentLength: parsed.contentLength,
        expiresInSeconds: HORSE_SCAN_UPLOAD_EXPIRY_SECONDS,
    });
    return { id: parsed.id, objectKey, uploadUrl };
}
/**
 * Verifies the uploaded photo actually landed in object storage, runs the
 * shared extraction port against it, and returns a confirm-screen draft —
 * purely a prefill mechanism. This use case never calls `addHorse` itself;
 * the client edits the draft into the ordinary add-horse form and submits
 * through the same validated `addHorse` path manual entry already uses
 * (`docs/products/stable.md` "Adding a horse": "saving is blocked until
 * every compulsory field is present, whether extracted or manually
 * filled").
 */
export async function scanHorseDocument(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = scanHorseDocumentSchema.parse(input);
    await requireHorseEditor(dependencies, parsed.stableId, actor.account.id);
    const uploads = dependencies.documentUploads;
    if (uploads === undefined)
        throw stableUploadUnavailable();
    const prefix = objectKeyPrefix(parsed.stableId);
    if (!parsed.objectKey.startsWith(`${prefix}${parsed.id}-`)) {
        throw stableForbidden();
    }
    const uploaded = await uploads.assertUploaded({
        objectKey: parsed.objectKey,
        contentType: parsed.contentType,
        contentLength: parsed.contentLength,
    });
    await uploads.claimUploaded({
        objectKey: parsed.objectKey,
        versionId: uploaded.versionId,
    });
    const extraction = dependencies.documentExtraction;
    if (extraction === undefined)
        throw stableExtractionUnavailable();
    const result = await extraction.extract({
        kind: parsed.kind,
        objectKey: parsed.objectKey,
        contentType: parsed.contentType,
    });
    const fields = buildHorseScanFields(parsed.kind, result.fields);
    const missingCompulsoryFields = missingCompulsoryHorseScanFields(fields);
    const now = dependencies.clock.now();
    const extractionId = dependencies.newId();
    await dependencies.repository.createHorseScanExtraction({
        id: extractionId,
        stableId: parsed.stableId,
        kind: parsed.kind,
        sourceObjectKey: parsed.objectKey,
        provider: result.provider,
        modelId: result.modelId,
        modelVersion: result.modelVersion,
        fields,
        actorId: actor.account.id,
        now,
    });
    return {
        extractionId,
        kind: parsed.kind,
        fields,
        missingCompulsoryFields,
        provider: result.provider,
        modelId: result.modelId,
        modelVersion: result.modelVersion,
        extractedAt: result.extractedAt,
    };
}
